/** PRIME setup flag — structural + accumulation + momentum + entry precision. */

import { calcRSI } from "./rsiScoring.js";

function nonEarningsOptionsBlock(input) {
  if (input?.optionsBlock !== true) return false;
  const flag = String(input.options_flag ?? input.optionsFlag ?? "");
  return !/^earnings/i.test(flag) && flag !== "earnings_block" && flag !== "earnings_unknown";
}

const PROX_EMA50 = 0.02;
const PROX_52W_HIGH = 0.01;

function emaLatest(closes, period) {
  if (!closes?.length || closes.length < period) return null;
  const chrono = [...closes].reverse();
  let ema = chrono.slice(0, period).reduce((s, v) => s + v, 0) / period;
  const k = 2 / (period + 1);
  for (let i = period; i < chrono.length; i++) {
    ema = chrono[i] * k + ema * (1 - k);
  }
  return ema;
}

function calcCMF(h, l, c, v, n = 20) {
  if (!c?.length || c.length < n) return null;
  let mfv = 0;
  let vol = 0;
  for (let i = 0; i < n; i++) {
    const hl = h[i] - l[i];
    if (!hl) continue;
    mfv += ((c[i] - l[i]) - (h[i] - c[i])) / hl * v[i];
    vol += v[i];
  }
  return vol ? mfv / vol : 0;
}

function calcObvSlope(closes, volumes) {
  if (!closes?.length || !volumes?.length || closes.length < 16) return null;
  let obv = 0;
  const obvArr = [0];
  for (let i = 0; i < closes.length - 1; i++) {
    if (closes[i] > closes[i + 1]) obv += volumes[i] || 0;
    else if (closes[i] < closes[i + 1]) obv -= volumes[i] || 0;
    obvArr.push(obv);
  }
  const recent = obvArr.slice(0, 5).reduce((s, v) => s + v, 0) / Math.min(5, obvArr.length);
  const prior = obvArr.slice(5, 15).reduce((s, v) => s + v, 0) / Math.min(10, obvArr.length - 5);
  return recent - prior;
}

function upDayVolumeRatio(closes, volumes, sessions = 10) {
  if (!closes?.length || !volumes?.length || closes.length < sessions + 1) return null;
  let upVol = 0;
  let upDays = 0;
  let totalVol = 0;
  for (let i = 0; i < sessions; i++) {
    const v = volumes[i] || 0;
    totalVol += v;
    if (closes[i] > closes[i + 1]) {
      upVol += v;
      upDays += 1;
    }
  }
  const avgAll = totalVol / sessions;
  const avgUp = upDays > 0 ? upVol / upDays : null;
  if (avgAll <= 0 || avgUp == null) return null;
  return avgUp / avgAll;
}

function rsiCrossAbove50WithinDays(closes, maxDays = 5) {
  if (!closes?.length || closes.length < 20) return null;
  for (let d = 0; d <= maxDays; d++) {
    const slice = closes.slice(d);
    if (slice.length < 16) break;
    const r0 = calcRSI(slice);
    const r1 = calcRSI(slice.slice(1));
    if (r0 != null && r1 != null && r1 < 50 && r0 >= 50) return d;
  }
  return null;
}

function donchianBreakoutConfirmed(highs, closes) {
  if (!highs?.length || highs.length < 21 || !closes?.length) return false;
  const upper = Math.max(...highs.slice(1, 21));
  return closes[0] > upper;
}

function resolveRange52w(input) {
  if (input.range52w?.high != null) return input.range52w;
  const closes = input.closes;
  if (!closes?.length) return null;
  const yr = closes.slice(0, Math.min(252, closes.length));
  const high = Math.max(...yr);
  const low = Math.min(...yr);
  const price = input.price ?? closes[0];
  return {
    high,
    low,
    distFromHigh: high ? ((price - high) / high) * 100 : null,
  };
}

function gateLabel(input) {
  const g = input.gate;
  if (typeof g === "string") return g;
  return g?.label ?? null;
}

function hasActiveModule3Top(input) {
  const sig = input?.signal ?? input?.moduleSignal ?? "";
  if (/TOP_CONFIRMED|TOP_FORMING/i.test(String(sig))) return true;
  const m3 = input?.module3;
  return !!(m3?.setupA?.triggered || m3?.setupB?.triggered || m3?.setupC?.active);
}

/**
 * @param {object} input — scan payload or runAnalysis result
 * @returns {{ isPrime: boolean, primeReasons: string[] }}
 */
export function evaluatePrimeSetup(input) {
  const reasons = [];
  if (!input?.closes?.length && input?.price == null) {
    return { isPrime: false, primeReasons: [] };
  }

  const closes = input.closes;
  const highs = input.highs;
  const lows = input.lows;
  const volumes = input.volumes;
  const price = input.price ?? input.livePrice ?? closes?.[0];

  const label = gateLabel(input);
  const gateOk = label === "GO" && !input.gate?.blocked;
  if (!gateOk) {
    return { isPrime: false, primeReasons: [] };
  }
  if (hasActiveModule3Top(input)) {
    return { isPrime: false, primeReasons: [] };
  }
  if (nonEarningsOptionsBlock(input)) {
    return { isPrime: false, primeReasons: [] };
  }
  if (input.rr1 != null && input.rr1 < 2) {
    return { isPrime: false, primeReasons: [] };
  }
  reasons.push("Gate GO");

  const ema50 = input.sm50 ?? input.ema50 ?? (closes ? emaLatest(closes, 50) : null);
  const ema200 = input.sm200 ?? input.ema200 ?? (closes ? emaLatest(closes, 200) : null);
  const structureOk =
    price != null && ema50 != null && ema200 != null && price > ema50 && ema50 > ema200;
  if (!structureOk) {
    return { isPrime: false, primeReasons: [] };
  }
  reasons.push("Price > EMA50 > EMA200");

  const cmf = input.cmf ?? (highs && lows && closes && volumes ? calcCMF(highs, lows, closes, volumes) : null);
  const obvSlope = calcObvSlope(closes, volumes);
  const volRatioUp = upDayVolumeRatio(closes, volumes);
  let accumHits = 0;
  if (cmf != null && cmf > 0.1) {
    accumHits += 1;
    reasons.push("CMF accumulation");
  }
  if (obvSlope != null && obvSlope > 0) {
    accumHits += 1;
    reasons.push("OBV rising");
  }
  if (volRatioUp != null && volRatioUp > 1.0) {
    accumHits += 1;
    reasons.push("Up-day volume > average");
  }
  if (accumHits < 2) {
    return { isPrime: false, primeReasons: [] };
  }

  const rsi = input.rsi ?? (closes ? calcRSI(closes) : null);
  const crossDays = closes ? rsiCrossAbove50WithinDays(closes, 5) : null;
  const momentumOk =
    rsi != null &&
    rsi >= 45 &&
    rsi <= 72 &&
    (rsi > 50 || crossDays != null);
  if (!momentumOk) {
    return { isPrime: false, primeReasons: [] };
  }
  if (rsi > 50) reasons.push("RSI above 50");
  else if (crossDays != null) reasons.push("RSI reclaimed 50");

  const move20d =
    closes?.length > 20 && closes[20] > 0
      ? ((closes[0] - closes[20]) / closes[20]) * 100
      : null;

  const confluence = input.confluence;
  const confluenceType =
    input.confluenceType ??
    (confluence?.active ? confluence.type ?? confluence.setupType : null);

  const donchianConfirmed =
    (confluence?.active && confluence.setupType === "DONCHIAN_BREAKOUT_CONFIRMED") ||
    input.donchianBreakout === true ||
    (highs && closes && donchianBreakoutConfirmed(highs, closes));

  const range52w = resolveRange52w(input);
  const near52wHigh =
    range52w?.distFromHigh != null && range52w.distFromHigh >= -PROX_52W_HIGH * 100;

  const notExtendedOk =
    (move20d == null || move20d < 20) &&
    (!near52wHigh || donchianConfirmed);
  if (!notExtendedOk) {
    return { isPrime: false, primeReasons: [] };
  }
  if (move20d != null && move20d < 20) reasons.push("Not extended (<20% 20d)");

  const fibLevel = input.fibLevel ?? null;
  const atFib = fibLevel != null;
  if (atFib) reasons.push(`At Fib ${fibLevel}`);

  const confluenceFiring =
    confluenceType != null &&
    confluence?.active === true &&
    ["FIB_CONFLUENCE_LONG", "DONCHIAN_BREAKOUT_CONFIRMED", "FIB_BOUNCE_DIVERGENCE"].includes(
      confluence.setupType ?? confluenceType
    );

  const nearEma50 =
    price != null && ema50 != null && ema50 > 0 && Math.abs(price - ema50) / ema50 <= PROX_EMA50;
  if (nearEma50) reasons.push("Pullback to EMA50");

  const entryOk = atFib || confluenceFiring || nearEma50 || donchianConfirmed;
  if (!entryOk) {
    return { isPrime: false, primeReasons: [] };
  }
  if (confluenceFiring && !atFib) {
    if (confluence.setupType === "DONCHIAN_BREAKOUT_CONFIRMED") reasons.push("Donchian breakout");
    else if (confluence.setupType === "FIB_CONFLUENCE_LONG") reasons.push("Fib confluence");
    else if (confluence.setupType === "FIB_BOUNCE_DIVERGENCE") reasons.push("Bounce confluence");
    else reasons.push("Confluence firing");
  } else if (donchianConfirmed && !reasons.some((r) => r.includes("Donchian"))) {
    reasons.push("Donchian breakout confirmed");
  }

  return { isPrime: true, primeReasons: [...new Set(reasons)] };
}

/** One or two Prime checks away — high-score GO setups only (watchlist / scanner hint). */
export function evaluateNearPrimeSetup(input) {
  if (evaluatePrimeSetup(input).isPrime) {
    return { nearPrime: false, missing: [] };
  }
  const buyScore = input?.buyScore ?? 0;
  if (buyScore < 6) return { nearPrime: false, missing: [] };
  const label = gateLabel(input);
  if (label !== "GO" || input?.gate?.blocked) return { nearPrime: false, missing: [] };
  if (hasActiveModule3Top(input) || nonEarningsOptionsBlock(input)) {
    return { nearPrime: false, missing: [] };
  }

  const missing = [];
  const closes = input?.closes;
  const highs = input?.highs;
  const lows = input?.lows;
  const volumes = input?.volumes;
  const price = input?.price ?? input?.livePrice ?? closes?.[0];

  if (input?.rr1 != null && input.rr1 < 2) {
    missing.push("R:R ≥ 2:1");
  }

  const ema50 = input.sm50 ?? input.ema50 ?? (closes ? emaLatest(closes, 50) : null);
  const ema200 = input.sm200 ?? input.ema200 ?? (closes ? emaLatest(closes, 200) : null);
  const structureOk =
    price != null && ema50 != null && ema200 != null && price > ema50 && ema50 > ema200;
  if (!structureOk) missing.push("Price > EMA50 > EMA200");

  const cmf = input.cmf ?? (highs && lows && closes && volumes ? calcCMF(highs, lows, closes, volumes) : null);
  const obvSlope = calcObvSlope(closes, volumes);
  const volRatioUp = upDayVolumeRatio(closes, volumes);
  let accumHits = 0;
  if (cmf != null && cmf > 0.1) accumHits += 1;
  if (obvSlope != null && obvSlope > 0) accumHits += 1;
  if (volRatioUp != null && volRatioUp > 1.0) accumHits += 1;
  if (accumHits < 2) missing.push("Need 2+ accumulation signals");

  const rsi = input.rsi ?? (closes ? calcRSI(closes) : null);
  const crossDays = closes ? rsiCrossAbove50WithinDays(closes, 5) : null;
  const momentumOk =
    rsi != null &&
    rsi >= 45 &&
    rsi <= 72 &&
    (rsi > 50 || crossDays != null);
  if (!momentumOk) missing.push("RSI momentum band");

  const move20d =
    closes?.length > 20 && closes[20] > 0
      ? ((closes[0] - closes[20]) / closes[20]) * 100
      : null;
  const confluence = input.confluence;
  const donchianConfirmed =
    (confluence?.active && confluence.setupType === "DONCHIAN_BREAKOUT_CONFIRMED") ||
    input.donchianBreakout === true ||
    (highs && closes && donchianBreakoutConfirmed(highs, closes));
  const range52w = resolveRange52w(input);
  const near52wHigh =
    range52w?.distFromHigh != null && range52w.distFromHigh >= -PROX_52W_HIGH * 100;
  const notExtendedOk =
    (move20d == null || move20d < 20) && (!near52wHigh || donchianConfirmed);
  if (!notExtendedOk) missing.push("Not extended / 52W high");

  const fibLevel = input.fibLevel ?? null;
  const atFib = fibLevel != null;
  const confluenceType =
    input.confluenceType ??
    (confluence?.active ? confluence.type ?? confluence.setupType : null);
  const confluenceFiring =
    confluenceType != null &&
    confluence?.active === true &&
    ["FIB_CONFLUENCE_LONG", "DONCHIAN_BREAKOUT_CONFIRMED", "FIB_BOUNCE_DIVERGENCE"].includes(
      confluence.setupType ?? confluenceType
    );
  const nearEma50 =
    price != null && ema50 != null && ema50 > 0 && Math.abs(price - ema50) / ema50 <= PROX_EMA50;
  const entryOk = atFib || confluenceFiring || nearEma50 || donchianConfirmed;
  if (!entryOk) missing.push("Entry at Fib / EMA50 / confluence");

  const nearPrime = missing.length > 0 && missing.length <= 2;
  return { nearPrime, missing };
}

/** Mutates payload/result with isPrime + primeReasons. */
export function attachPrimeSetup(input) {
  const { isPrime, primeReasons } = evaluatePrimeSetup(input);
  input.isPrime = isPrime;
  input.primeReasons = primeReasons;
  return input;
}

export const PRIME_STAR_TOOLTIP =
  "Prime Setup — Gate GO, accumulation confirmed, and price at a key entry level. Highest conviction setup.";

/** Watchlist Action column — where to enter when isPrime. */
export function getPrimeWatchlistActionText(result) {
  if (result?.isPrime !== true) return null;
  const setup = result.confluence?.active ? result.confluence.setupType : null;
  if (setup === "DONCHIAN_BREAKOUT_CONFIRMED") {
    return "⚡ DONCHIAN BREAK — ENTER";
  }
  if (result.fibLevel) {
    const fib = String(result.fibLevel).includes("%") ? result.fibLevel : `${result.fibLevel}%`;
    return `📍 AT FIB ${fib} — ENTER`;
  }
  const reasons = result.primeReasons || [];
  if (reasons.some((r) => /Pullback to EMA50|EMA50/i.test(r))) {
    return "📉 AT EMA50 — ENTER";
  }
  if (setup === "FIB_CONFLUENCE_LONG") return "📍 AT FIB — ENTER";
  if (setup === "FIB_BOUNCE_DIVERGENCE") return "📍 FIB BOUNCE — ENTER";
  if (reasons.some((r) => /Donchian/i.test(r))) return "⚡ DONCHIAN BREAK — ENTER";
  if (reasons.some((r) => /^At Fib/i.test(r))) {
    const m = reasons.find((r) => /^At Fib/i.test(r));
    const pct = m?.replace(/^At Fib\s*/i, "") || "";
    return pct ? `📍 AT FIB ${pct} — ENTER` : "📍 AT FIB — ENTER";
  }
  return "⭐ PRIME — ENTER";
}

const FIB_SWING_KEY = {
  "38.2%": "fib382",
  "50%": "fib500",
  "61.8%": "fib618",
  "78.6%": "fib786",
};

/** Human-readable entry anchor for briefing + watchlist. */
export function formatPrimeEntryLevel(result) {
  if (!result) return "—";
  const swing = result.fibSwing;
  const level = result.fibLevel;
  if (level && swing) {
    const key = FIB_SWING_KEY[level] || FIB_SWING_KEY[String(level).replace(/%$/, "") + "%"];
    const px = key ? swing[key] : null;
    if (px != null && Number.isFinite(px)) {
      return `Fib ${level} @ $${px.toFixed(2)}`;
    }
    return `Fib ${level}`;
  }
  const setup = result.confluence?.active ? result.confluence.setupType : null;
  if (setup === "DONCHIAN_BREAKOUT_CONFIRMED") {
    const don = result.confluence?.conditions?.find((c) => c.key === "donchian");
    if (don?.detail) return `Donchian — ${don.detail}`;
    return "Donchian breakout level";
  }
  if (result.sm50 != null && Number.isFinite(result.sm50)) {
    const reasons = result.primeReasons || [];
    if (reasons.some((r) => /EMA50/i.test(r)) || result.isPrime) {
      return `EMA50 @ $${result.sm50.toFixed(2)}`;
    }
  }
  if (result.price != null) return `Near $${result.price.toFixed(2)}`;
  return "—";
}
